import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { PageEnum, TABLEPAI, OMS_FORMAT } from "@/enums/pageEnum";
import { useTranslation } from "react-i18next";
import { Button, Radio, Skeleton } from "antd";
import { useCallback, useState, useRef, useEffect, useTransition } from "react";
import { useUrlPath } from "@/hooks";
import { getCount } from "@api";
import { BasicTable, HView, PropsRef, Status } from "@/components";
import { LiftTcLongSuffix } from "@/utils/commonConfig";
import NProgress from "nprogress";
import useMessageRedux from "@/store/message-redux";
import { parseQueryString, parseMonth } from "@/utils/toolt";
import dayjs from "@/utils/dayjsConfig";

interface CountInfo {
  openCnt?: number;
  closeCnt?: number;
  delayCnt?: number;
}

export default function OMSSchedule() {
  const { t } = useTranslation();
  const { pathname, state } = useLocation();
  const navigation = useNavigate();
  const tableRef = useRef<PropsRef>(null);
  const [isPending, startTransition] = useTransition();
  const { deleteNameToUrl, updateNameToUrl } = useUrlPath();
  const [groupParams, setGroupParams] = useState<string>("ALL");
  const [data, setData] = useState<CountInfo>();
  const { message } = useMessageRedux();

  const getRadioGroup = useCallback(
    (name: React.ReactNode, value: string, number?: number) => {
      return (
        <Radio.Button
          value={value}
          className="text-center px-12 border-0 h-auto"
        >
          <p className="text-[#00000073] dark:text-[#ffffffd9] text-[14px]">
            {name}
          </p>
          {isPending ? (
            <Skeleton.Button active className="h-5" size={"small"} />
          ) : (
            <p className="text-[24px] font-medium">{number || 0}</p>
          )}
        </Radio.Button>
      );
    },
    [isPending]
  );

  const getPathParams = useCallback(() => parseMonth(parseQueryString()), []);

  const getDataCount = useCallback(() => {
    setTimeout(() => {
      NProgress.done();
    }, 1000);
    startTransition(async () => {
      try {
        const res = await getCount();
        setData(res.data?.body);
      } catch (error: any) {
        message.error(error?.detail || error);
      }
    });
  }, [message]);

  const callBackFunc = () => {
    getDataCount();
  };

  useEffect(() => {
    getDataCount();
  }, [getDataCount]);

  useEffect(() => {
    const parms = getPathParams();
    if (pathname === PageEnum.OMS_SCHEDULE) {
      setGroupParams(parms.status || "ALL");
    }
    if (state && state.isReload) {
      getDataCount();
      navigation(PageEnum.OMS_SCHEDULE, { replace: true });
    }
  }, [pathname, getPathParams, state, getDataCount, navigation]);

  const getTimeForamt = useCallback(
    (title: string, dataIndex: string) => {
      return {
        title: t(title),
        dataIndex,
        renderText: (text: number) =>
          text ? dayjs(text).format(OMS_FORMAT) : "",
      };
    },
    [t]
  );

  if (pathname === `${PageEnum.OMS_SCHEDULE}/add-new`) return <Outlet />;

  return (
    <>
      <div className="p-4">
        <div className="bg-[#fff] dark:bg-[#141414] p-4 mb-4 flex justify-between items-center">
          <div className="flex justify-start items-center">
            <div className="text-[18px] font-medium pr-5 text-black dark:text-[#ffffffd9]">
              {t("omsSched")}
            </div>
            <div>
              <Radio.Group
                onChange={({ target }) => {
                  const val = target.value;
                  if (val === "ALL" || val === "DELAY") {
                    navigation(deleteNameToUrl("status"), { replace: true });
                  } else {
                    navigation(updateNameToUrl("status", val), {
                      replace: true,
                    });
                  }
                  setGroupParams(val);
                  tableRef.current?.afreshTableData(
                    val === "DELAY" ? { page: 1, delay: true } : { page: 1 }
                  );
                }}
                value={groupParams}
                className="omsSchedRadio"
              >
                {getRadioGroup(t("open"), "OPEN", data?.openCnt)}
                {getRadioGroup(t("closed"), "CLOSED", data?.closeCnt)}
                {getRadioGroup(t("onDelay"), "DELAY", data?.delayCnt)}
                {getRadioGroup(
                  t("history"),
                  "ALL",
                  (data?.openCnt ?? 0) + (data?.closeCnt ?? 0)
                )}
              </Radio.Group>
            </div>
          </div>
          <div>
            <Button
              type="primary"
              onClick={() => navigation(`${PageEnum.OMS_SCHEDULE}/add-new`)}
            >
              {t("addNew")}
            </Button>
          </div>
        </div>
        <BasicTable
          columns={[
            { title: t("lift"), dataIndex: "liftName" },
            { title: t("LIFT_COMPANY"), dataIndex: "liftCompany" },
            getTimeForamt("schOff", "offAt"),
            getTimeForamt("schOn", "onAt"),
            getTimeForamt("swiTime", "switchOnAt"),
            {
              title: t("actual"),
              dataIndex: "duration",
              renderText: (text: number) => <>{text || ""}</>,
            },
            {
              title: t("delayDay"),
              dataIndex: "delay",
              renderText: (text: number, record: { status: string }) => (
                <>{record?.status !== "CLOSED" ? text || "" : ""}</>
              ),
            },
            {
              title: t("status"),
              dataIndex: "statusFormat",
              renderText: (text: string) => <Status status={text} />,
            },
            {
              title: t("action"),
              dataIndex: "action",
              renderText: (_: string, record: { id: string }) => (
                <HView
                  name={t("view")}
                  path={`${PageEnum.OMS_SCHEDULE}/detail/${record?.id}${
                    groupParams !== "ALL" ? `?status=${groupParams}` : ""
                  }`}
                  state={record}
                />
              ),
            },
          ]}
          filterTableFields={[
            { ...LiftTcLongSuffix(t) },
            {
              field: "liftPostalCode",
              label: t("postalCode"),
              component: "text",
            },
          ]}
          filterSubmit={() => {
            setGroupParams("ALL");
          }}
          resetSubmit={() => {
            setGroupParams("ALL");
          }}
          ref={tableRef}
          isDefaultToolBar={false}
          isShowCheckbox={false}
          className="min-h-[calc(100vh-276px)]"
          headerTitle={() => t("schedules")}
          requestUrl={TABLEPAI.OMS_PAGE}
        />
      </div>
      {pathname.includes(`${PageEnum.OMS_SCHEDULE}/detail/`) && (
        <Outlet context={{ callBackFunc }} />
      )}
    </>
  );
}
